/**
 * Query-state derivation for the Similar Players tab (SE-7580).
 *
 * The tab used to read only `data` and `loading` from its three Apollo
 * queries. `error` was never destructured, so a failed or transport-starved
 * read arrived as `data === undefined` + `loading === false` — pixel-identical
 * to a successful read that returned nothing — and the UI asserted "no
 * similar players found matching your filters" / "No Leagues". That is a
 * positive claim the data never supported.
 *
 * These helpers turn the raw hook result into a discriminated union so every
 * outcome is a distinct, exhaustively-handled state. They are pure so the
 * mapping can be tested without rendering, and so the "which state wins"
 * ordering below is stated once instead of being re-derived per component.
 *
 * ## What the wire can and cannot distinguish
 *
 * CAN: "the read failed" vs "the read succeeded and returned nothing". The
 * Apollo client sets `errorPolicy: "all"` on watch queries
 * (`providers/ApolloClient.tsx`), so a GraphQL or network failure RESOLVES
 * with an `error` on the hook result rather than rejecting — the failure is
 * observable, it was simply never read. (A `catch` would never have run; see
 * SE-6278 for the same trap.) Client-aborted requests — a POV team switch
 * calling `resetStore` — are swallowed by the error link as `{ data: null }`
 * and correctly produce no error here.
 *
 * CANNOT: "your organization has no leagues" vs "your entitlements filtered
 * every league out". `listMyOrganizationsLeagues` returns a bare connection
 * with no permission discriminator — no `requiredTier`, no gate marker, no
 * nullable-with-reason — so a permission-withheld result is indistinguishable
 * on the wire from a genuinely empty one. The same holds for
 * `listSimilarPlayers`, whose `SimilarPlayerConnection` carries only `edges`
 * and `pageInfo`. A "not permitted" state is therefore NOT modelled here: it
 * would be invented, not observed. Expressing it needs a backend change of
 * the `ComplianceGatedQuantity.requiredTier` shape (tracked with SE-7562 /
 * SE-7566), not a client-side guess.
 */

import type {
  SimilarPlayersListState,
  SimilarPlayersOptionsState,
} from "@/features/player-detail/types";
import type { SimilarPlayersPlayerResultFragment } from "@/generated/graphql";

/** Shared empty tuple so the picker resolver returns a stable reference. */
const NO_OPTIONS: readonly never[] = [];

/** Inputs to {@link deriveSimilarPlayersListState}. */
interface SimilarPlayersListStateInput {
  /** Players already extracted from the query result (may be empty). */
  readonly players: readonly SimilarPlayersPlayerResultFragment[];
  /**
   * Whether an answer is still outstanding. TRUE for every in-flight
   * `networkStatus` (initial load, `setVariables` from a filter change,
   * refetch, fetchMore) AND while the query is skipped for want of a
   * `playerId` — in both cases an absent result is not evidence of zero
   * results.
   */
  readonly isPending: boolean;
  /** Whether the hook surfaced a GraphQL or network error. */
  readonly hasError: boolean;
}

/**
 * Maps a Similar Players query result onto its resolution state.
 *
 * Precedence, and why:
 * 1. Results in hand win — a user with rows on screen is shown those rows.
 *    An error alongside them downgrades `ready` to `stale` so the staleness
 *    is disclosed rather than swallowed.
 * 2. In-flight beats a stale error, so retrying a failure shows progress
 *    instead of pinning the old error on screen.
 * 3. An error with nothing to show is `failed` — never `empty`.
 * 4. Only a settled, error-free, empty read may claim `empty`.
 * @param input - Players, pending flag, and error flag from the hook.
 * @param input.players - Players already extracted from the query result.
 * @param input.isPending - Whether an answer is still outstanding.
 * @param input.hasError - Whether the hook surfaced an error.
 * @returns The single state that the tab should render.
 */
export const deriveSimilarPlayersListState = ({
  players,
  isPending,
  hasError,
}: SimilarPlayersListStateInput): SimilarPlayersListState => {
  if (players.length > 0) {
    return hasError ? { kind: "stale", players } : { kind: "ready", players };
  }
  if (isPending) {
    return { kind: "loading" };
  }
  if (hasError) {
    return { kind: "failed" };
  }
  return { kind: "empty" };
};

/** Inputs to {@link deriveOptionsState}. */
interface OptionsStateInput<T> {
  /** Options already extracted from the query result (may be empty). */
  readonly options: readonly T[];
  /** Whether the options query is in flight. */
  readonly isLoading: boolean;
  /** Whether the options query surfaced a GraphQL or network error. */
  readonly hasError: boolean;
}

/**
 * Maps a filter-options query result (leagues, nationalities) onto its
 * resolution state, using the same precedence as the list above.
 * @param input - Options, loading flag, and error flag from the hook.
 * @param input.options - Options already extracted from the query result.
 * @param input.isLoading - Whether the options query is in flight.
 * @param input.hasError - Whether the hook surfaced an error.
 * @returns The state the picker should render.
 */
export const deriveOptionsState = <T>({
  options,
  isLoading,
  hasError,
}: OptionsStateInput<T>): SimilarPlayersOptionsState<T> => {
  if (options.length > 0) {
    // Cached options plus an error is `stale`, never `ready`: returning
    // `ready` here would re-discard the error the moment anything was cached,
    // and the picker would present a possibly-outdated list as current.
    return hasError ? { kind: "stale", options } : { kind: "ready", options };
  }
  if (isLoading) {
    return { kind: "loading" };
  }
  if (hasError) {
    return { kind: "failed" };
  }
  return { kind: "ready", options };
};

/** Picker props derived from a {@link SimilarPlayersOptionsState}. */
export interface OptionPickerProps<T> {
  /** Options to render in the dropdown. */
  readonly data: readonly T[];
  /** Whether to show the picker's loading skeleton. */
  readonly loading: boolean;
  /**
   * Error copy for the dropdown's empty slot. `undefined` leaves the picker's
   * own "No <title>" copy in place, which is only correct once the read has
   * actually succeeded.
   */
  readonly errorMessage?: string;
  /**
   * Disclosure copy for options that ARE shown but whose refresh failed. Set
   * only for `stale`, so a cached list is never passed off as current.
   */
  readonly noticeMessage?: string;
}

/**
 * Flattens an options state into the props the shared MultiSelect takes.
 *
 * Exhaustive by construction: the `never` fallthrough means a state added to
 * `SimilarPlayersOptionsState` later fails to compile here instead of
 * silently falling back to the "no options" rendering — the failure mode this
 * ticket exists to remove.
 * @param state - The options query's resolution state.
 * @param errorMessage - Copy shown when the read failed with nothing to show.
 * @param noticeMessage - Copy shown alongside options whose refresh failed.
 * @returns Props for the shared MultiSelect picker.
 */
export const resolveOptionPickerProps = <T>(
  state: SimilarPlayersOptionsState<T>,
  errorMessage: string,
  noticeMessage: string
): OptionPickerProps<T> => {
  switch (state.kind) {
    case "loading":
      return { data: NO_OPTIONS, loading: true };
    case "failed":
      return { data: NO_OPTIONS, loading: false, errorMessage };
    case "stale":
      return { data: state.options, loading: false, noticeMessage };
    case "ready":
      return { data: state.options, loading: false };
    default: {
      const exhaustive: never = state;
      return exhaustive;
    }
  }
};
